/**
 * retryInterceptor.ts - API 요청 재시도 인터셉터
 * 
 * 네트워크 오류나 일시적인 서버 오류에 대한 자동 재시도 기능을 제공합니다.
 */

import { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// 재시도 설정 인터페이스
export interface RetryConfig {
  retries: number;
  retryDelay: (retryCount: number, error?: AxiosError) => number;
  retryCondition: (error: AxiosError) => boolean;
  shouldResetTimeout: boolean;
  onRetry?: (retryCount: number, error: AxiosError, requestConfig: InternalAxiosRequestConfig) => void;
}

// 기본 재시도 설정
export const defaultRetryConfig: RetryConfig = {
  retries: 3,
  retryDelay: (retryCount: number) => {
    // Exponential backoff with jitter
    const baseDelay = Math.pow(2, retryCount - 1) * 1000;
    const jitter = Math.random() * 0.1 * baseDelay;
    return Math.min(baseDelay + jitter, 10000); // 최대 10초
  },
  retryCondition: (error: AxiosError) => {
    // 네트워크 에러 또는 5xx 서버 오류인 경우만 재시도
    if (!error.response) {
      return true; // 네트워크 에러
    }
    
    const status = error.response.status;
    return status >= 500 && status < 600; // 5xx 서버 오류
  },
  shouldResetTimeout: false,
  onRetry: undefined,
};

// 요청 설정에 재시도 정보 추가를 위한 타입 확장
declare module 'axios' {
  export interface AxiosRequestConfig {
    __retryCount?: number;
    retryConfig?: Partial<RetryConfig>;
  }
}

// 재시도 인터셉터 설정
export const setupRetryInterceptor = (
  axiosInstance: AxiosInstance,
  config: Partial<RetryConfig> = {}
): void => {
  const retryConfig: RetryConfig = { ...defaultRetryConfig, ...config };

  axiosInstance.interceptors.response.use(
    // 성공 응답은 그대로 반환
    (response) => response,
    
    // 에러 응답에 대한 재시도 로직
    async (error: AxiosError) => {
      const originalConfig = error.config as InternalAxiosRequestConfig & { __retryCount?: number; retryConfig?: Partial<RetryConfig> };
      
      if (!originalConfig) {
        return Promise.reject(error);
      }

      // 현재 요청의 재시도 설정 (요청별 설정이 우선)
      const currentRetryConfig: RetryConfig = {
        ...retryConfig,
        ...(originalConfig.retryConfig || {}),
      };

      // 재시도 횟수 초기화
      originalConfig.__retryCount = originalConfig.__retryCount || 0;

      // 재시도 조건 확인
      const shouldRetry = 
        originalConfig.__retryCount < currentRetryConfig.retries &&
        currentRetryConfig.retryCondition(error);

      if (!shouldRetry) {
        return Promise.reject(error);
      }

      // 재시도 횟수 증가
      originalConfig.__retryCount += 1;

      // 재시도 콜백 호출
      if (currentRetryConfig.onRetry) {
        currentRetryConfig.onRetry(originalConfig.__retryCount, error, originalConfig);
      }

      // 재시도 지연 시간 계산
      const retryDelay = currentRetryConfig.retryDelay(originalConfig.__retryCount, error);

      // 타임아웃 재설정
      if (currentRetryConfig.shouldResetTimeout && originalConfig.timeout) {
        originalConfig.timeout = originalConfig.timeout;
      }

      // 지연 후 재시도
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(axiosInstance(originalConfig));
        }, retryDelay);
      });
    }
  );
};

// 특정 요청에 대한 재시도 설정을 위한 유틸리티
export const withRetry = <T = any>(
  config: InternalAxiosRequestConfig,
  retryConfig: Partial<RetryConfig>
): InternalAxiosRequestConfig & { retryConfig: Partial<RetryConfig> } => {
  return {
    ...config,
    retryConfig,
  };
};

// 재시도 상태를 확인하는 유틸리티
export const getRetryCount = (config: InternalAxiosRequestConfig): number => {
  const retryCount = (config as any).__retryCount;
  return typeof retryCount === 'number' ? retryCount : 0;
};

// 재시도 가능 여부를 확인하는 유틸리티
export const isRetryableError = (error: AxiosError): boolean => {
  return defaultRetryConfig.retryCondition(error);
};